import { AlertOctagon, CheckCircle2, Clock, FlaskConical, Loader2, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { SEVERITY_LABEL, STATUS_LABEL } from '../../lib/format';
import type { ReviewStatus, Severity } from '../../types/api';

export function Badge({ children, className, title }: { children: ReactNode; className?: string; title?: string }) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        className ?? 'bg-raised text-muted ring-line',
      )}
    >
      {children}
    </span>
  );
}

const SEV_CLASS: Record<Severity, string> = {
  critical: 'bg-sev-critical/10 text-sev-critical ring-sev-critical/30',
  high: 'bg-sev-high/10 text-sev-high ring-sev-high/30',
  medium: 'bg-sev-medium/10 text-sev-medium ring-sev-medium/30',
  low: 'bg-sev-low/10 text-sev-low ring-sev-low/30',
  info: 'bg-sev-info/10 text-sev-info ring-sev-info/30',
};

export const SEV_DOT: Record<Severity, string> = {
  critical: 'bg-sev-critical',
  high: 'bg-sev-high',
  medium: 'bg-sev-medium',
  low: 'bg-sev-low',
  info: 'bg-sev-info',
};

export function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <Badge className={SEV_CLASS[severity]}>
      <span className={cn('h-1.5 w-1.5 rounded-full', SEV_DOT[severity])} aria-hidden />
      {SEVERITY_LABEL[severity]}
    </Badge>
  );
}

const STATUS_STYLE: Record<ReviewStatus, { cls: string; icon: ReactNode }> = {
  queued: { cls: 'bg-raised text-muted ring-line', icon: <Clock className="h-3 w-3" aria-hidden /> },
  processing: { cls: 'bg-chalk-blue/15 text-chalk-blue ring-chalk-blue/40', icon: <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> },
  completed: { cls: 'bg-ok/10 text-ok ring-ok/30', icon: <CheckCircle2 className="h-3 w-3" aria-hidden /> },
  failed: { cls: 'bg-danger/10 text-danger ring-danger/30', icon: <XCircle className="h-3 w-3" aria-hidden /> },
};

export function StatusBadge({ status }: { status: ReviewStatus }) {
  const s = STATUS_STYLE[status];
  return (
    <Badge className={s.cls}>
      {s.icon}
      {STATUS_LABEL[status]}
    </Badge>
  );
}

/** Marks results that came from the deterministic demo reviewer rather than the AI reviewer. */
export function DemoBadge({ className }: { className?: string }) {
  return (
    <Badge
      title="Sample data from demo mode — not an AI Pilot analysis"
      className={cn('bg-chalk-yellow/15 text-chalk-yellow ring-chalk-yellow/40', className)}
    >
      <FlaskConical className="h-3 w-3" aria-hidden />
      Demo data
    </Badge>
  );
}

export function PotentialBadge() {
  return (
    <Badge title="AI-generated finding. Verify before acting." className="bg-raised text-muted ring-line">
      <AlertOctagon className="h-3 w-3" aria-hidden />
      Potential issue
    </Badge>
  );
}
